import {
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { compatWrapper } from '@backstage/core-compat-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';

import { ConfidenceProvider } from '@spotify-confidence/react';
import { Banner, ProgressCircle, Text } from '@spotify-internal/encore-web';
import { useRefreshBillingAfterCheckout } from '@spotify/confidence-billing-react';
import {
  ConfidenceCoreProvider,
  LoginPage,
  ResourceLinkExtensions,
  TokenAccessRecovery,
  getCurrentLocationPath,
  hasOAuthCallbackParams,
  useAccessTokenAcquisition,
  useAuth,
  useRegion,
  useSentryBillingContext,
  useSentryScope,
} from '@spotify/confidence-core-react';
import type { AccountSetupStatus } from '@spotify/confidence-core-react';
import {
  FlagEvaluationProvider,
  useFeatureFlag,
} from '@spotify/confidence-flag-evaluation-react';
import {
  getTypeOrNull,
  useAccountSetupQuery,
} from '@spotify/confidence-graphql';
import { TourResumer } from '@spotify/confidence-onboarding-react';
import { defaultResourceLinkExtensionElements } from '@spotify/plugin-confidence-app-module-extensions';
import {
  AppAccountRedirect,
  AppRedirectPage,
  useAppRedirect,
} from '@spotify/plugin-confidence-app-react';
import { RunningChatsProvider } from '@spotify/plugin-confidence-chat-react';
import { FlagResourceLinkExtension } from '@spotify/plugin-confidence-flags';
import { PulseCaptureNotificationsProvider } from '@spotify/plugin-confidence-pulse-react';
import {
  PageTrackingGuard,
  PageTrackingProvider,
  useAutoTracking,
  useInferredTracking,
} from '@spotify/plugin-confidence-tracking-react';
import { WorkflowCreditAvailabilityProvider } from '@spotify/plugin-confidence-workflows-react';

import { confidenceApi } from '../apis/Apis';
import { RecentActivityResourceSetup } from '../recentActivityResources';
import {
  clearInvitationLoginReturn,
  consumeInvitationLoginReturnRoute,
} from '../utils/invitationLoginReturn';
import {
  getOrganizationInvitationRoute,
  isPublicRoute,
} from '../utils/publicRoutes';
import { FlagContext } from './FlagContext';

import styles from './RootWrapper.module.css';

const resourceLinkExtensions = (
  <ResourceLinkExtensions>
    <FlagResourceLinkExtension />
    {defaultResourceLinkExtensionElements}
  </ResourceLinkExtensions>
);

const LoadingPage = () => (
  <div className={styles.loading}>
    <ProgressCircle aria-label="Loading application" size="medium" />
  </div>
);

const BlockedAccountBanner = ({ blocked }: { blocked?: boolean | null }) => {
  if (!blocked) {
    return null;
  }

  return (
    <div className={styles.blockedBanner}>
      <Banner colorSet="negative" contextual role="alert">
        <Text variant="bodyMedium">
          <strong>This account has been blocked.</strong> Please contact support
          for assistance.
        </Text>
      </Banner>
    </div>
  );
};

const AuthenticatedRoot = ({ children }: PropsWithChildren) => {
  const autoTrackRef = useAutoTracking();
  useInferredTracking(
    useFeatureFlag<{ enabled: boolean }>('inferred-ui-tracking', {
      enabled: false,
    }),
  );

  const { data, error, loading } = useAccountSetupQuery({
    fetchPolicy: 'cache-and-network',
  });
  const currentAccount = getTypeOrNull(data?.currentAccount);
  const serviceTier = currentAccount?.serviceTier;

  let accountSetupStatus: AccountSetupStatus = 'unavailable';
  if (currentAccount) {
    accountSetupStatus = 'loaded';
  } else if (loading) {
    accountSetupStatus = 'loading';
  } else if (error) {
    accountSetupStatus = 'error';
  }

  useSentryBillingContext({ accountSetupStatus, serviceTier });

  return (
    <div ref={autoTrackRef}>
      <PageTrackingGuard />
      <RecentActivityResourceSetup />
      {resourceLinkExtensions}
      <BlockedAccountBanner blocked={currentAccount?.blocked} />
      <AppAccountRedirect />
      <TourResumer />
      <WorkflowCreditAvailabilityProvider>
        {children}
      </WorkflowCreditAvailabilityProvider>
    </div>
  );
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

type LoginRedirectOptions = NonNullable<
  Parameters<ReturnType<typeof useAuth>['loginWithRedirect']>[0]
>;

const Root = ({ children }: PropsWithChildren) => {
  useRefreshBillingAfterCheckout();
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    error,
    identity,
    orgName,
  } = useAuth();
  const preferredUrl = useFeatureFlag(
    'preferred-app-for-org.preferred-url',
    '',
  );
  const appRedirect = useAppRedirect(preferredUrl);
  const region = useRegion();
  const {
    error: accessTokenError,
    retry: retryAccessToken,
    retrying,
  } = useAccessTokenAcquisition();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loginStartError, setLoginStartError] = useState<Error>();
  const [loginStarting, setLoginStarting] = useState(false);
  const [invitationReturnRoute, setInvitationReturnRoute] = useState<string>();
  const hasAttemptedOrgLoginRef = useRef(false);
  const invitationRoute = getOrganizationInvitationRoute(location);

  const shouldLogin = searchParams.get('login') === 'true';
  const organization = searchParams.get('organization') ?? undefined;
  const isWaitingForAccessToken = isAuthenticated && !region;

  useSentryScope({ isAuthenticated, identity, orgName, region });

  const startLogin = useCallback(
    ({
      authorizationParams,
      omitFromReturnTo = [],
    }: {
      authorizationParams?: LoginRedirectOptions['authorizationParams'];
      omitFromReturnTo?: string[];
    } = {}) => {
      setLoginStartError(undefined);
      setLoginStarting(true);
      void loginWithRedirect({
        authorizationParams,
        appState: {
          returnTo: getCurrentLocationPath(omitFromReturnTo),
        },
      }).catch((loginError: unknown) => {
        setLoginStartError(toError(loginError));
        setLoginStarting(false);
      });
    },
    [loginWithRedirect],
  );

  useEffect(() => {
    if (isAuthenticated) {
      clearInvitationLoginReturn();
      setInvitationReturnRoute(undefined);
      return;
    }

    if (location.pathname === '/invitation') {
      setInvitationReturnRoute(undefined);
      return;
    }

    if (error && hasOAuthCallbackParams(searchParams)) {
      setInvitationReturnRoute(consumeInvitationLoginReturnRoute());
    }
  }, [error, isAuthenticated, location.pathname, searchParams]);

  useEffect(() => {
    if (
      hasAttemptedOrgLoginRef.current ||
      isLoading ||
      isAuthenticated ||
      !shouldLogin ||
      !organization
    ) {
      return;
    }

    hasAttemptedOrgLoginRef.current = true;
    startLogin({
      authorizationParams: { organization },
      omitFromReturnTo: ['login', 'organization'],
    });
  }, [shouldLogin, organization, isLoading, isAuthenticated, startLogin]);

  if (invitationRoute) {
    return <Navigate to={invitationRoute} replace  data-track-source-anchor="b42cb6aa00;root-navigate"/>;
  }

  if (isPublicRoute(location.pathname)) {
    return <Fragment>{children}</Fragment>;
  }

  if (!isLoading && isWaitingForAccessToken && accessTokenError) {
    return (
      <TokenAccessRecovery
        onRetry={retryAccessToken}
        onSignIn={() =>
          startLogin({ authorizationParams: { prompt: 'login' } })
        }
        error={loginStartError}
        retrying={retrying}
        signingIn={loginStarting}
      />
    );
  }

  if (isLoading || isWaitingForAccessToken) {
    return <LoadingPage />;
  }

  if (invitationReturnRoute) {
    return <Navigate to={invitationReturnRoute} replace  data-track-source-anchor="dc350b0263;root-navigate-2"/>;
  }

  if (!isAuthenticated) {
    return (
      <LoginPage
        onLogin={() => startLogin()}
        onUseDifferentAccount={() =>
          startLogin({ authorizationParams: { prompt: 'login' } })
        }
        loading={isLoading || loginStarting}
        error={loginStartError ?? error}
      />
    );
  }

  if (appRedirect.shouldRedirect) {
    return <AppRedirectPage targetUrl={appRedirect.targetUrl} />;
  }

  return <AuthenticatedRoot>{children}</AuthenticatedRoot>;
};

export const RootWrapper = AppRootWrapperBlueprint.make({
  name: 'AppRootWrapper',
  params: {
    component: ({ children }) => {
      return compatWrapper(
        <ConfidenceProvider confidence={confidenceApi}>
          <FlagEvaluationProvider fallback={<LoadingPage />}>
            <PageTrackingProvider>
              <ConfidenceCoreProvider>
                <FlagContext>
                  <PulseCaptureNotificationsProvider>
                    <RunningChatsProvider>
                      <Root>{children}</Root>
                    </RunningChatsProvider>
                  </PulseCaptureNotificationsProvider>
                </FlagContext>
              </ConfidenceCoreProvider>
            </PageTrackingProvider>
          </FlagEvaluationProvider>
        </ConfidenceProvider>,
      );
    },
  },
});
